<script>
    import { Chart, registerables } from 'chart.js';
    import { Tooltip } from 'sveltestrap';
    import Spinner from 'svelte-spinner';

    import { formatDate, getApiDateFormat, isToday, getIsoDateStringAsDate } from '../../date-time.js';
    import { vptServer, token, reportDate, selectedAspect, inmateCount } from '../../stores.js';
    import { config } from "../../config";
    import { createRequestParams } from "../../string.js";

    export let requestProperties;

    let chart;
    Chart.register(...registerables);

    let overviewData;
    let preventAutorender = true;
    
    let reportDateDescription;
    $: { if ($reportDate != undefined) { 
            reportDateDescription = formatDate(getApiDateFormat($reportDate));
        }
    }

    $: {
        if ($config.surveyUrn && requestProperties && requestProperties.requestDate) {
            getSurveyOverview(requestProperties.requestDate + createRequestParams(requestProperties.requestParams));
        }
    }

    async function getSurveyOverview(requestString) {
        if (!preventAutorender) {
            try 
            {
                overviewData = null;
                chart.destroy();
            }
            catch {
            }
            preventAutorender = true;
        }

        await fetch(`${$vptServer}/establishment/survey/overview/${requestString}`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
            .then(r => {
                if (!r.ok) {
                    throw Error(r.status);
                }
                return r;
            })
            .then(r => r.json())
            .then(data => {
                overviewData = data;
            })
            .catch(error => {
                console.error("Error getting establishment survey overview");
                console.error(error);
                return;
            });

            renderSurveyChart();

            // Allow filter changes to update the graph
            preventAutorender = false;
    }

    function renderSurveyChart() {
        if (overviewData && overviewData.countTotalInmates > 0) {
            var ctx = document.getElementById("survey-chart-canvas").getContext("2d");
            chart = new Chart(ctx, {
                type: 'pie',
                data: {
                    labels: ['High', 'Medium','Low'],
                    datasets: [{
                        label: 'Survey Summary',
                        data: [overviewData.countHighLevels, overviewData.countMediumLevels, overviewData.countLowLevels],
                        backgroundColor: ['#dc3545', '#ffc107', '#28a745'],
                    }]
                },
                options: {
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                label: function(context) {
                                    return context.label + ": " + context.parsed;
                                }
                            }
                        }
                    }
                }
            });
        }
    }
</script>

<style>
    .establishment-widget {
        width: 200px;
    }

    .widget-content {
        align-items: center;
    }

    .chart-widget {
        height: 75px;
    }

    .establishment-widget .no-records {
	    padding: 10px 15px 0 15px;
    }

    /* The default layout of the widget must be adjusted to account for the Overdue label if it is displayed */
    .count-widget-body-compact {
        padding-top: 0;
    }

    .count-widget-body-compact span.targeted-count {
        font-size: 27px;
        line-height: 29px;
    }

    .count-widget-body-compact span.reduced-text {
        line-height: 13px;
    }

    .count-widget-body-compact span.overdue-text {
        color: #676a6d;
        line-height: 16px;
    }
</style>

{#if $config.surveyUrn}
    <div class="establishment-widget">
        {#if overviewData && (overviewData.countTotalInmates > 0 || overviewData.countOverdueInmates > 0) && !($inmateCount.filtered > $inmateCount.unfiltered)}
            <button on:click class="button-reset focus-outline card" class:selected={$selectedAspect === $config.surveyLabelName}>
                <div class="card-body">
                    <div class="widget-container">
                        <span class="widget-label">{$config.surveyLabelName}</span>

                        {#if (overviewData.countTotalInmates == 0 && overviewData.countOverdueInmates > 0)}
                            <div class="widget-content">
                                <div class="card-flex-column">
                                    <span class="no-records">No {$config.surveyLabelName} surveys have been completed in the {isToday($reportDate) ? 'last' : 'previous'} {$config.surveyPastDaysToAnalyse} days.</span>
                                    <span class="no-records">There are {overviewData.countOverdueInmates} overdue.</span>
                                </div>
                            </div>
                        {:else}
                            <div class="widget-content widget-pad-right">
                                <div class="chart-widget chart-widget-narrow">
                                    <canvas id="survey-chart-canvas"></canvas>
                                </div>

                                <div class="count-widget">
                                    <div class="count-widget-body" class:count-widget-body-compact={overviewData.countOverdueInmates > 0}>
                                        <div class="card-flex-column">
                                            <div>
                                                <span class="targeted-count">{overviewData.countTargetedInmates}</span>
                                            </div>

                                            <div><span class="reduced-text">of</span></div>
                                            <div><span><b>{overviewData.countTotalInmates}</b> targeted</span></div>
                                            
                                            {#if overviewData.countOverdueInmates > 0}
                                                <div><span class="reduced-text overdue-text">{overviewData.countOverdueInmates} overdue</span></div>
                                            {/if}
                                        </div>
                                    </div>

                                    <div class="count-widget-footer">
                                        <span id="percentage-indicator-survey"
                                                class:percentage-indicator-up={overviewData.trendIndicatorTargetedInmates == 'U'}
                                                class:percentage-indicator-down={overviewData.trendIndicatorTargetedInmates == 'D'}>
                                            ({overviewData.percentageTargetedInmates})%
                                        </span>
                                        <Tooltip target="percentage-indicator-survey" placement="bottom">{overviewData.countTargetedInmates} of {overviewData.countTotalInmates} {$config.inmateLabelName.toLowerCase()}s who completed the survey in the {isToday($reportDate) ? 'last' : 'previous'} {$config.surveyPastDaysToAnalyse} days {isToday($reportDate) ? '' : 'before ' + reportDateDescription} {isToday($reportDate) ? 'are' : 'were'} targeted</Tooltip>

                                        {#if overviewData.trendIndicatorTargetedInmates == 'U'}
                                            <span id="trend-indicator-survey-u">
                                                <img src="/assets/arrow/arrow-up-danger.svg" alt="Increasing Trend" />
                                            </span>
                                            <Tooltip target="trend-indicator-survey-u" placement="bottom">Increasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.surveyPastDaysToAnalyse} days an average of {overviewData.trendPercentageTargetedInmates}% were targeted</Tooltip>
                                        {:else if overviewData.trendIndicatorTargetedInmates == 'D'}
                                            <span id="trend-indicator-survey-d">
                                                <img src="/assets/arrow/arrow-down-success.svg" alt="Decreasing Trend" />
                                            </span>
                                            <Tooltip target="trend-indicator-survey-d" placement="bottom">Decreasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.surveyPastDaysToAnalyse} days an average of {overviewData.trendPercentageTargetedInmates}% were targeted</Tooltip>
                                        {/if}
                                    </div>
                                </div>
                            </div>
                        {/if}
                    </div>
                </div>
            </button>
        {:else if overviewData}
            <div class="card" class:selected={$selectedAspect === $config.surveyLabelName}>
                <div class="card-body">
                    <div class="widget-container">
                        <span class="widget-label">{$config.surveyLabelName}</span>
                        
                        <div class="widget-content">
                            {#if ($inmateCount.filtered > $inmateCount.unfiltered) || ($inmateCount.unfiltered == 0 && isToday(getIsoDateStringAsDate(requestProperties.requestDate)))}
                                <span class="no-records">Data is being regenerated.</span>
                            {:else}
                                <span class="no-records">No {$config.surveyLabelName} surveys have been completed in the {isToday($reportDate) ? 'last' : 'previous'} {$config.surveyPastDaysToAnalyse} days.</span>
                            {/if}
                        </div>
                    </div>
                </div>
            </div>
        {:else}
            <div class="card" class:selected={$selectedAspect === $config.surveyLabelName}> 
                <div class="card-body">
                    <div class="widget-container">
                        <div class="widget-spinner">
                            <Spinner size="100" speed="750" color="#3d5980" thickness="3" gap="15" />
                        </div>
                    </div>
                </div>
            </div>
        {/if}
    </div>
{/if}
